import { useEffect } from 'react';
import { Card, CardBody, Spinner, Table, CardFooter, Button } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import './PendingTravelRequest.scss';
import { mdiPlus } from '@mdi/js';
import Icon from '@mdi/react';
import { fetchTravelRequestPending } from '../../../store/apis/travelService';
import { fetchTableColumnValues } from '../../../store/apis/commonService';
import { travelRequestHeaders } from './constants';
import { renderStatusColors } from '../../HRTravelRequest/BookingAgent/constants';

import useTableHook from '../../../components/hooks/useTableHook';
import {
  Badge,
  FilterDropdown,
  TableFilterBubbles,
  TableSortDisplayArrow,
  TablePagination,
} from '../../../components/Common';
import { AuthUtil } from '../../../utils';
import { Messages } from '../../../common/messages';

function PendingTravelRequest() {
  document.title = 'Travel Request | Envision - Daikin';
  const navigate = useNavigate();
  const {
    data,
    columnData,
    pageRequest,
    resetFilters,
    setPageRequest,
    applyFilter,
    columnName,
    setColumnName,
    setPageLoadTrigger,
    clearSingleFilterValue,
    handleManualSort,
    checkColumnIfSorted,
    isLoadingData,
    isLoadingColumn,
    nextPage,
    prevPage,
    getPageSize,
    setPageSize,
    handleAutoSort,
  } = useTableHook({
    defaultPageRequest: {
      pageNumber: 1,
      pageSize: 50,
      sortColumn: 'RequestNo',
      sortDirection: 'DESC',
      filters: [],
    },
    fetchFn: fetchTravelRequestPending,
    fetchColumnFn: fetchTableColumnValues,
    queryKeyPrefix: 'TravelRequest',
    columnKeyPrefix: 'TravelRequestColumn',
    tableName: 'TravelRequest',
    defaultExternalFilters: {
      employeeId: {
        value: AuthUtil.getAuthUser()?.employeeId,
        label: AuthUtil.getAuthUser()?.userFullName,
      },
    },
    tableQueryOptions: {
      staleTime: 0,
      cacheTime: 0,
      refetchOnMount: 'always',
      refetchOnWindowFocus: false,
      keepPreviousData: false,
    },
  });

  useEffect(() => {
    setPageLoadTrigger(true);
  }, [setPageLoadTrigger]);

  const renderTableBody = () => {
    const tableData = data?.data;

    if (!tableData || tableData.length === 0) {
      return (
        <tr>
          <td className="text-center" colSpan={5}>
            {isLoadingData ? (
              <>
                <Spinner color="dark" size="sm" />
                <span className="ms-2">Loading...</span>
              </>
            ) : (
              Messages.NO_TRAVEL
            )}
          </td>
        </tr>
      );
    }

    return tableData.map(item => (
      <tr
        key={item?.RequestNo}
        onClick={() =>
          navigate(
            `/travel-request/edit/${item?.RequestNo}/${item?.RevisionNo}`,
          )
        }
        className="cursor-pointer"
      >
        <td>
          <button
            type="button"
            className="btn btn-link text-decoration-underline p-0 user-id"
            onClick={() =>
              navigate(
                `/travel-request/edit/${item?.RequestNo}/${item?.RevisionNo}`,
              )
            }
          >
            {item?.RequestNo}
          </button>
        </td>
        <td>{item?.TravelDate}</td>
        <td>{item?.TravelDestination}</td>
        <td>{item?.TravelPurpouse}</td>
        <td>
          <Badge config={renderStatusColors[item.Status]} dot>
            {item.Status}
          </Badge>
        </td>
      </tr>
    ));
  };

  return (
    <div className="page-content main-table-page">
      <Card className="enquiry-card">
        <CardBody className="p-0">
          {/* Title & Buttons */}
          <div className="d-flex flex-row justify-content-between align-items-center px-3 pt-3 pb-0">
            <div className="fw-semibold fs-5">Travel Request</div>
            <div className="d-flex flex-row align-items-center gap-2">
              <Button
                type="button"
                onClick={() => navigate('/travel-request/new')}
                className="custom-btn"
              >
                <span>New Request</span>
                <Icon path={mdiPlus} size={0.8} />
              </Button>
            </div>
          </div>

          {/* Filter Bubbles */}
          <div className="my-2 px-2">
            <TableFilterBubbles
              allFilters={pageRequest?.filters || []}
              clearSingleFilterValue={clearSingleFilterValue}
              labelMapping={travelRequestHeaders}
              clearAllFilters={resetFilters}
            />
          </div>

          {/* Table */}
          <div
            className="table-responsive table-container"
            id="pending-tr-table-container"
          >
            <Table hover className="custom-table align-middle text-nowrap">
              <thead>
                <tr>
                  {Object.entries(travelRequestHeaders).map(([key, value]) => (
                    <th key={key} className="px-2">
                      <div className="d-flex flex-row align-items-center justify-content-start gap-2">
                        <div
                          className="p-0 cursor-pointer"
                          onClick={() => {
                            handleAutoSort(key);
                          }}
                        >
                          {value}
                        </div>
                        <TableSortDisplayArrow
                          direction={checkColumnIfSorted(key)}
                        />
                        <button
                          className="p-0"
                          type="button"
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#fff',
                          }}
                          onClick={e => {
                            e.stopPropagation();
                            setColumnName(key);
                          }}
                        >
                          <FilterDropdown
                            handleSetColumnName={() => {
                              setColumnName(key);
                            }}
                            setColumnName={setColumnName}
                            columnData={columnData}
                            applyFilter={applyFilter}
                            columnName={columnName}
                            pageRequest={pageRequest}
                            setPageRequest={setPageRequest}
                            handleManualSort={handleManualSort}
                            isLoading={isLoadingColumn}
                          />
                        </button>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>{renderTableBody()}</tbody>
            </Table>
          </div>
        </CardBody>
        <CardFooter className="py-2">
          <TablePagination
            nextPage={nextPage}
            prevPage={prevPage}
            totalPages={data?.totalPages}
            currentPage={data?.currentPage}
            hasPreviousPage={data?.hasPreviousPage}
            hasNextPage={data?.hasNextPage}
            getPageSize={getPageSize}
            setPageSize={setPageSize}
            totalCount={data?.totalCount}
          />
        </CardFooter>
      </Card>
    </div>
  );
}

export default PendingTravelRequest;
